import { useEffect, useState, useMemo } from 'react';
import { Product } from '../types';

export interface LiveAmazonItem {
  price: string;
  availability: string;
}

export interface LiveAmazonDataMap {
  [asin: string]: LiveAmazonItem;
}

export function useLiveAmazonData(products: Product[]) {
  const [liveData, setLiveData] = useState<LiveAmazonDataMap>({});
  const [loading, setLoading] = useState<boolean>(true);

  // Extract and sort ASINs to create a primitive string dependency
  const asinsString = useMemo(() => {
    const asinsSet = new Set<string>();
    products.forEach((p) => {
      if (p.asin && p.asin.trim() !== '') {
        asinsSet.add(p.asin.trim());
      }
    });
    return Array.from(asinsSet).sort().join(',');
  }, [products]);

  useEffect(() => {
    if (!asinsString) {
      setLiveData({});
      setLoading(false);
      return;
    }

    const asins = asinsString.split(',');
    // Batch ASINs into groups of 10
    const batches: string[][] = [];
    for (let i = 0; i < asins.length; i += 10) {
      batches.push(asins.slice(i, i + 10));
    }

    let active = true;

    const fetchAllBatches = async () => {
      setLoading(true);
      const map: LiveAmazonDataMap = {};

      const fetchBatch = async (batch: string[]) => {
        try {
          const response = await fetch('/api/amazon/items', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ asins: batch }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          if (Array.isArray(data)) {
            data.forEach((item: any) => {
              if (item.asin) {
                map[item.asin] = {
                  price: item.price || '',
                  availability: item.availability || '',
                };
              }
            });
          }
        } catch (err) {
          console.error('Failed to fetch Amazon items batch:', batch, err);
        }
      };

      // Fetch all batches in parallel
      await Promise.all(batches.map((batch) => fetchBatch(batch)));

      if (active) {
        setLiveData(map);
        setLoading(false);
      }
    };

    fetchAllBatches();

    return () => {
      active = false;
    };
  }, [asinsString]);

  return { liveData, loading };
}
